import {
  Calculate as CalculateIcon,
  FileDownload as FileDownloadIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  Divider,
  Grid,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
} from '@mui/material';
import { LightTooltip } from 'lib/designSystem/WhiteTooltip';
import { formatMoney } from 'lib/functions/utils';
import React from 'react';

import {
  ConfirmingRequestForCreation,
  OperationDetailsEarlyPayment,
} from '../../../../types';

const SimpleTooltip = (tableData: { name: string; value: string }[]) => (
  <Table size="small">
    <TableBody>
      {tableData.map((row) => (
        <TableRow key={row.name}>
          <TableCell>{row.name}</TableCell>
          <TableCell align="right">{row.value}</TableCell>
        </TableRow>
      ))}
    </TableBody>
  </Table>
);

interface SimulationEarlyPaymentProps {
  expanded: boolean;
  onAccordionChange: (
    panel: string,
  ) => (event: React.SyntheticEvent, isExpanded: boolean) => void;
  requestForCreation: ConfirmingRequestForCreation;
  operationDetails: OperationDetailsEarlyPayment;
  onExportSimulation: () => Promise<void>;
}

const SimulationEarlyPayment: React.FC<SimulationEarlyPaymentProps> = ({
  expanded,
  onAccordionChange,
  requestForCreation,
  operationDetails,
  onExportSimulation,
}) => {
  const {
    operationAmount,
    operationCostRate,
    operationCostComissionFixed,
    operationCostComissionVariable,
    operationToDeposit,
    operationIva,
    expensesIva,
    expensesCost,
    rate,
    fundRate: fundRateRaw,
  } = operationDetails;

  const fundRate = fundRateRaw || 0;
  const spread = rate > 0 ? (rate - fundRate).toFixed(2) : 0;

  const operationCostFundRate = Math.round(
    operationCostRate * (fundRate / rate),
  );
  const operationCostSpread = Math.round(
    operationCostRate * (Number(spread) / rate),
  );

  const allInCost =
    operationCostRate +
    expensesCost +
    operationCostComissionFixed +
    operationCostComissionVariable +
    operationIva +
    expensesIva;

  return (
    <Accordion
      expanded={expanded}
      onChange={onAccordionChange('panel1')}
      sx={{
        boxShadow: 'none',
        '&:before': { display: 'none' },
        '& .MuiAccordionSummary-root': {
          minHeight: 'auto',
          padding: '16px 20px',
          backgroundColor: 'white',
          '&.Mui-expanded': { minHeight: 'auto' },
        },
        '& .MuiAccordionSummary-content': {
          margin: 0,
          '&.Mui-expanded': { margin: 0 },
        },
        '& .MuiAccordionDetails-root': {
          padding: '0 20px 20px',
          backgroundColor: 'white',
        },
      }}
    >
      <AccordionSummary
        expandIcon={null}
        sx={{
          '& .MuiAccordionSummary-expandIconWrapper': { transform: 'none' },
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{ width: '100%' }}
        >
          <Box
            sx={{
              backgroundColor: 'primary.main',
              borderRadius: '50%',
              p: 0.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
            }}
          >
            <CalculateIcon sx={{ fontSize: '1rem' }} />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: 'text.primary',
                fontSize: '1rem',
                lineHeight: 1.1,
              }}
            >
              Detalles de Simulación
            </Typography>
            <Chip
              label="Pago Anticipado"
              size="small"
              color="primary"
              variant="outlined"
              sx={{ height: '18px', fontSize: '0.65rem' }}
            />
          </Box>
          {requestForCreation.amount > 0 && (
            <Box sx={{ textAlign: 'right' }}>
              <Stack
                direction="row"
                alignItems="start"
                justifyContent="flex-end"
                spacing={0.75}
              >
                <Typography variant="h4" color="primary">
                  {formatMoney(requestForCreation.amount)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  CLP
                </Typography>
              </Stack>
            </Box>
          )}
          <IconButton
            size="small"
            onClick={() => void onExportSimulation()}
            sx={{
              color: 'primary.main',
              backgroundColor: 'grey.50',
              border: '1px solid',
              borderColor: 'grey.200',
              '&:hover': {
                backgroundColor: 'grey.100',
                borderColor: 'grey.300',
              },
              borderRadius: '100px',
              padding: '4px 12px',
            }}
          >
            <FileDownloadIcon sx={{ fontSize: '1rem', paddingRight: '4px' }} />
            <Typography variant="caption" fontWeight={600}>
              Exportar
            </Typography>
          </IconButton>
        </Stack>
      </AccordionSummary>

      <AccordionDetails>
        <Box
          sx={{
            backgroundColor: 'grey.50',
            p: 1.5,
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'grey.200',
            mb: 2,
          }}
        >
          <Grid container spacing={1}>
            {/* Monto operación */}
            <Grid item xs={12}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography variant="caption" color="text.secondary">
                  Monto operación
                </Typography>
                <Typography variant="caption" fontWeight={600}>
                  {formatMoney(operationAmount)} CLP
                </Typography>
              </Stack>
            </Grid>

            {/* Diferencia de precio con desglose por documento */}
            <Grid item xs={12}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <Typography variant="caption" color="text.secondary">
                    Diferencia precio{' '}
                    {requestForCreation.exchangeDays !== undefined &&
                    requestForCreation.exchangeDays !== 0
                      ? `(${requestForCreation.exchangeDays > 0 ? '+' : ''}${requestForCreation.exchangeDays} días)`
                      : ''}{' '}
                    ({rate || 0}%)
                  </Typography>
                </Stack>
                <Typography variant="caption" fontWeight={600}>
                  {formatMoney(operationCostRate)} CLP
                </Typography>
              </Stack>
            </Grid>

            {/* Desglose fondo / spread */}
            <Grid item xs={12}>
              <Box sx={{ pl: 3, mt: 0.5 }}>
                <Grid container spacing={0.5}>
                  {fundRate > 0 && (
                    <Grid item xs={12}>
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                      >
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontSize: '0.65rem' }}
                        >
                          Tasa del fondo ({fundRate}%)
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontSize: '0.65rem' }}
                        >
                          {formatMoney(operationCostFundRate)} CLP
                        </Typography>
                      </Stack>
                    </Grid>
                  )}
                  <Grid item xs={12}>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontSize: '0.65rem' }}
                      >
                        Spread ({spread}%)
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontSize: '0.65rem' }}
                      >
                        {formatMoney(operationCostSpread)} CLP
                      </Typography>
                    </Stack>
                  </Grid>
                </Grid>
              </Box>
            </Grid>

            {/* Comisión operación con desglose por documento */}
            <Grid item xs={12}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <Typography variant="caption" color="text.secondary">
                    Comisión operación + IVA
                  </Typography>
                  <LightTooltip
                    title={
                      <>
                        {SimpleTooltip([
                          {
                            name: 'Costo fijo',
                            value: formatMoney(operationCostComissionFixed),
                          },
                          {
                            name: 'Costo variable',
                            value: formatMoney(operationCostComissionVariable),
                          },
                          { name: 'IVA', value: formatMoney(operationIva) },
                        ])}
                      </>
                    }
                  >
                    <InfoIcon
                      sx={{ fontSize: '0.7rem', color: 'text.secondary' }}
                    />
                  </LightTooltip>
                </Stack>
                <Typography variant="caption" fontWeight={600}>
                  {formatMoney(
                    operationCostComissionFixed +
                      operationCostComissionVariable +
                      operationIva,
                  )}{' '}
                  CLP
                </Typography>
              </Stack>
            </Grid>

            {/* Otros gastos con desglose por documento */}
            {expensesCost !== 0 && (
              <Grid item xs={12}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <Typography variant="caption" color="text.secondary">
                      Otros gastos{expensesIva > 0 ? ' + IVA' : ''}
                    </Typography>
                    <LightTooltip
                      title={
                        <>
                          {SimpleTooltip([
                            { name: 'Costo', value: formatMoney(expensesCost) },
                            ...(expensesIva > 0
                              ? [
                                  {
                                    name: 'IVA',
                                    value: formatMoney(expensesIva),
                                  },
                                ]
                              : []),
                          ])}
                        </>
                      }
                    >
                      <InfoIcon
                        sx={{ fontSize: '0.7rem', color: 'text.secondary' }}
                      />
                    </LightTooltip>
                  </Stack>
                  <Typography variant="caption" fontWeight={600}>
                    {formatMoney(expensesCost + expensesIva)} CLP
                  </Typography>
                </Stack>
              </Grid>
            )}

            <Grid item xs={12}>
              <Divider />
            </Grid>

            {/* Monto a girar con desglose completo por documento */}
            <Grid item xs={12}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <Typography variant="body2" fontWeight={600}>
                    Monto a girar
                  </Typography>
                  <LightTooltip
                    title={
                      <>
                        {SimpleTooltip([
                          {
                            name: 'Costo total',
                            value: formatMoney(allInCost),
                          },
                        ])}
                      </>
                    }
                  >
                    <InfoIcon
                      sx={{ fontSize: '0.8rem', color: 'text.secondary' }}
                    />
                  </LightTooltip>
                </Stack>
                <Typography variant="body2" fontWeight={700} color="primary">
                  {formatMoney(operationToDeposit)} CLP
                </Typography>
              </Stack>
            </Grid>
          </Grid>
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};

export default SimulationEarlyPayment;
